import type { AxiosInstance } from 'axios';

import type {
  CreateFlashDealRequest,
  UpdateFlashDealRequest,
  AddFlashDealProductRequest,
  UpdateFlashDealProductRequest,
  CreateCouponRequest,
  UpdateCouponRequest,
  VendorOfferRequest,
  OfferFunding,
  OfferStatus,
} from '@terra/contracts';

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

/**
 * A coupon as the storefront may show it. Deliberately missing the usage
 * counters that {@link Coupon} carries — those are campaign internals.
 */
export interface PublicCoupon {
  id: string;
  code: string;
  title: string | null;
  discountType: 'PERCENTAGE' | 'FLAT';
  discountAmount: number;
  minSubtotalPaise: number;
  maxDiscountPaise: number | null;
  endsAt: string | null;
}

export interface Coupon {
  id: string;
  code: string;
  title: string | null;
  discountType: 'PERCENTAGE' | 'FLAT';
  discountAmount: number;
  active: boolean;
  startsAt: string | null;
  endsAt: string | null;
  minSubtotalPaise: number;
  maxDiscountPaise: number | null;
  usageLimit: number | null;
  perUserLimit: number | null;
  usedCount: number;
  createdAt: string;
  updatedAt: string;
  _count?: { redemptions: number };
}

/** Where an offer is in its life. Derived from its status and dates. */
export type OfferPhase =
  | 'PENDING_APPROVAL'
  | 'SCHEDULED'
  | 'LIVE'
  | 'ENDED'
  | 'REJECTED'
  | 'CANCELLED';

/** A seller's offer, with each product's price under it and who pays the discount. */
export interface SellerOffer {
  id: string;
  title: string;
  vendor: { id: string; storeName: string; slug: string } | null;
  startsAt: string;
  endsAt: string;
  funding: OfferFunding;
  platformShareBps: number;
  status: OfferStatus;
  phase: OfferPhase;
  sellerNote: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  items: Array<{
    productId: string;
    title: string;
    slug: string;
    imageUrl: string | null;
    discountType: 'PERCENTAGE' | 'FLAT';
    /** Basis points for PERCENTAGE, paise for FLAT. */
    discountAmount: number;
    /** For the lowest-priced variant. */
    pricePaise: number;
    offerPricePaise: number;
    sellerFundsPaise: number;
    platformFundsPaise: number;
  }>;
}

export function marketingEndpoints(http: AxiosInstance) {
  return {
    // ── seller offers ──
    myOffers: () => unwrap<SellerOffer[]>(http.get('/vendor/offers')),
    submitOffer: (data: VendorOfferRequest) =>
      unwrap<SellerOffer>(http.post('/vendor/offers', data)),
    resubmitOffer: (id: string, data: VendorOfferRequest) =>
      unwrap<SellerOffer>(http.patch(`/vendor/offers/${id}`, data)),
    cancelOffer: (id: string) => unwrap<SellerOffer>(http.post(`/vendor/offers/${id}/cancel`)),
    // ── platform review ──
    offersForReview: (params?: { status?: OfferStatus }) =>
      unwrap<SellerOffer[]>(http.get('/admin/offers', { params })),
    approveOffer: (id: string, note?: string) =>
      unwrap<SellerOffer>(http.post(`/admin/offers/${id}/approve`, note ? { note } : {})),
    rejectOffer: (id: string, note: string) =>
      unwrap<SellerOffer>(http.post(`/admin/offers/${id}/reject`, { note })),

    listFlashDeals: (params?: { active?: boolean }) =>
      unwrap<any>(http.get('/admin/marketing/flash-deals', { params })),
    getActiveFlashDeals: () => unwrap<any>(http.get('/marketing/flash-deals/active')),
    createFlashDeal: (data: CreateFlashDealRequest) =>
      unwrap<any>(http.post('/admin/marketing/flash-deals', data)),
    updateFlashDeal: (id: string, data: UpdateFlashDealRequest) =>
      unwrap<any>(http.patch(`/admin/marketing/flash-deals/${id}`, data)),
    deleteFlashDeal: (id: string) => unwrap<any>(http.delete(`/admin/marketing/flash-deals/${id}`)),
    listFlashDealProducts: (flashDealId: string) =>
      unwrap<any[]>(http.get(`/admin/marketing/flash-deals/${flashDealId}/products`)),
    addFlashDealProduct: (flashDealId: string, data: AddFlashDealProductRequest) =>
      unwrap<any>(http.post(`/admin/marketing/flash-deals/${flashDealId}/products`, data)),
    updateFlashDealProduct: (
      flashDealId: string,
      productId: string,
      data: UpdateFlashDealProductRequest,
    ) =>
      unwrap<any>(
        http.patch(`/admin/marketing/flash-deals/${flashDealId}/products/${productId}`, data),
      ),
    removeFlashDealProduct: (flashDealId: string, productId: string) =>
      unwrap<any>(http.delete(`/admin/marketing/flash-deals/${flashDealId}/products/${productId}`)),

    // Coupons
    /** Public: the live coupons, for a storefront coupon strip. */
    getActiveCoupons: () => unwrap<PublicCoupon[]>(http.get('/marketing/coupons/active')),
    listCoupons: (params?: { active?: boolean }) =>
      unwrap<Coupon[]>(http.get('/admin/marketing/coupons', { params })),
    createCoupon: (data: CreateCouponRequest) =>
      unwrap<Coupon>(http.post('/admin/marketing/coupons', data)),
    updateCoupon: (id: string, data: UpdateCouponRequest) =>
      unwrap<Coupon>(http.patch(`/admin/marketing/coupons/${id}`, data)),
    deleteCoupon: (id: string) => unwrap<any>(http.delete(`/admin/marketing/coupons/${id}`)),

    // Dynamic Popups
    listPopups: () => unwrap<any[]>(http.get('/admin/marketing/popups')),
    getActivePopups: () => unwrap<any[]>(http.get('/marketing/popups/active')),
    createPopup: (data: any) => unwrap<any>(http.post('/admin/marketing/popups', data)),
    updatePopup: (id: string, data: any) =>
      unwrap<any>(http.patch(`/admin/marketing/popups/${id}`, data)),
    deletePopup: (id: string) => unwrap<any>(http.delete(`/admin/marketing/popups/${id}`)),

    // Newsletters
    listNewsletters: (params?: { kind?: 'NEWSLETTER' | 'TEMPLATE' }) =>
      unwrap<any[]>(http.get('/admin/marketing/newsletters', { params })),
    createNewsletter: (data: any) => unwrap<any>(http.post('/admin/marketing/newsletters', data)),
    updateNewsletter: (id: string, data: any) =>
      unwrap<any>(http.patch(`/admin/marketing/newsletters/${id}`, data)),
    deleteNewsletter: (id: string) =>
      unwrap<any>(http.delete(`/admin/marketing/newsletters/${id}`)),

    // Subscribers
    listSubscribers: () => unwrap<any[]>(http.get('/admin/marketing/subscribers')),
    createSubscriber: (data: any) => unwrap<any>(http.post('/admin/marketing/subscribers', data)),
    updateSubscriber: (id: string, data: any) =>
      unwrap<any>(http.patch(`/admin/marketing/subscribers/${id}`, data)),
    deleteSubscriber: (id: string) =>
      unwrap<any>(http.delete(`/admin/marketing/subscribers/${id}`)),
  };
}

export type MarketingEndpoints = ReturnType<typeof marketingEndpoints>;
